/**
 * Whether this person has hidden the assistant's corner character.
 *
 * A personal, per-browser preference, like the theme: hiding the doodle changes nothing
 * for colleagues and nothing on the server. The assistant itself stays one click away in
 * the account menu, so hiding the character never hides the feature.
 *
 * Storage can throw (private windows, blocked site data), so every access is guarded and
 * the default is "shown".
 */

import { useCallback, useEffect, useState } from 'react';

const KEY = 'assistant-doodle-hidden';
const EVENT = 'assistant-doodle-visibility';

function readHidden(): boolean {
  try {
    return window.localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

export function useAssistantDoodleHidden(): [boolean, (hidden: boolean) => void] {
  const [hidden, setHiddenState] = useState(readHidden);

  useEffect(() => {
    // The menu and the corner are separate components: keep both in step, in this tab
    // through a custom event and across tabs through the storage event.
    const sync = () => setHiddenState(readHidden());
    window.addEventListener(EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const setHidden = useCallback((next: boolean) => {
    try {
      if (next) window.localStorage.setItem(KEY, '1');
      else window.localStorage.removeItem(KEY);
    } catch {
      // Not persisted; still applied for this page view below.
    }
    setHiddenState(next);
    window.dispatchEvent(new Event(EVENT));
  }, []);

  return [hidden, setHidden];
}

/** Ask the assistant panel to open its drawer, from anywhere in the shell. */
export const OPEN_ASSISTANT_EVENT = 'assistant-open';

export function openAssistant(): void {
  window.dispatchEvent(new Event(OPEN_ASSISTANT_EVENT));
}
